import React, { useState } from 'react';
import { useUserSettings } from '../../context/UserSettingsContext';
import { Icons } from '../icons/Icons';
import CableTypeIcon from '../icons/CableTypeIcon';
import { TERMINAL_DATA, TERMINAL_GROUPS } from '../../constants/terminalConstants';
import { renderTerminalIconSimple } from '../../utils/terminalRenderLogic';
import { MACHINES, getMachineList } from '../../constants/machineConstants';
import { EW3035_ADVANCED_SETTINGS_DEFAULTS, omitProjectSettingKeys } from '../../machines/EW-3035+L';
import { isGoogleDriveConfigured } from '../../services/googleDrive';
import CloudFolderPickerModal from './CloudFolderPickerModal';
import EW3035AdvancedSettingsPanel from './EW3035AdvancedSettingsPanel';
import { useI18n } from '../../context/I18nContext';
import { LanguageToggle } from '../ui/LanguageToggle';
import CableCatalogItemCard from '../ui/CableCatalogItemCard';
import { getDefaultCableCatalog } from '../../utils/getDefaultSettings';
import { settingsAPI } from '../../services/api';
import { getColorOptionsForLanguage, translateColorForDisplay } from '../../utils/colorUtils';
import {
  changeCatalogAWGGauge,
  changeCatalogSectionStandard,
  findCatalogAWGSectionByMetric,
  getCatalogSectionStandard,
  getConfiguredAWGSections,
  matchesCatalogSearch,
  normalizeCatalogItemSection,
  normalizeCatalogItemColors,
  normalizeCableDescription,
  validateCatalogItem
} from '../../utils/cableCatalog';
import { SEZIONI_AWG } from '../../constants/cableConstants';
import {
  formatLogoTimestamp,
  loadCurrentUserLogoPreviewUrl,
  LOGO_ACCEPT_ATTRIBUTE,
  prepareLogoUploadPayload
} from '../../utils/logoUtils';

const createCatalogItemId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createUnipolarCableItem = () => ({
  id: createCatalogItemId('uni'),
  produttore: '',
  colore: '',
  sezione: '',
  standardSezione: 'metric',
  codiceCostruttore: '',
  codiceInterno: '',
  descrizione: ''
});

const createMultipolarCableItem = () => ({
  id: createCatalogItemId('multi'),
  produttore: '',
  sezione: '',
  standardSezione: 'metric',
  codiceCostruttore: '',
  codiceInterno: '',
  descrizione: '',
  numeroPoli: 0,
  coloreGuaina: '',
  poliColori: []
});

const normalizePoleCount = (value) => {
  if (value === '') return '';

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : Math.max(parsed, 0);
};

const syncPoleColorsLength = (poleColors = [], poleCount) => {
  const nextPoleCount = typeof poleCount === 'number' ? poleCount : normalizePoleCount(poleCount);
  const safeCount = typeof nextPoleCount === 'number' ? nextPoleCount : 0;
  const nextColors = Array.isArray(poleColors) ? [...poleColors] : [];

  if (nextColors.length > safeCount) {
    return nextColors.slice(0, safeCount);
  }

  while (nextColors.length < safeCount) {
    nextColors.push('');
  }

  return nextColors;
};

const createDefaultProfileData = () => ({
  username: '',
  email: '',
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  logo: null
});

const terminalSupportsIconVariants = (terminalCode) => Object.prototype.hasOwnProperty.call(TERMINAL_DATA[terminalCode] || {}, 'iconVariant');

// Parametri numerici di lavorazione del terminale (mm).
const TERMINAL_NUMERIC_FIELDS = ['len', 'peel', 'printOffset', 'multiLen', 'multiPeel', 'multiPrintOffset'];

// Tutti i parametri confrontabili con il default: servono a segnalare i terminali
// personalizzati, cosi' un valore rimasto indietro rispetto ai default e' visibile.
const TERMINAL_COMPARABLE_FIELDS = ['nome', 'minSection', 'maxSection', 'allowsMultiple', 'iconVariant', ...TERMINAL_NUMERIC_FIELDS];

const isTerminalCustomized = (terminalCode, terminal) => {
  const defaultTerminal = TERMINAL_DATA[terminalCode];
  if (!defaultTerminal || !terminal) return false;

  return TERMINAL_COMPARABLE_FIELDS.some(field => (terminal[field] ?? null) !== (defaultTerminal[field] ?? null));
};

export const UserSettingsPage = ({ onClose }) => {
  const { settings, saveSettings, updateSettings } = useUserSettings();
  const { t, language } = useI18n();
  const [activeSection, setActiveSection] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveMessageType, setSaveMessageType] = useState('success');
  const [folderPickerProvider, setFolderPickerProvider] = useState('');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);
  const [logoMessage, setLogoMessage] = useState('');
  const [logoMessageType, setLogoMessageType] = useState('success');
  const logoInputRef = React.useRef(null);

  // Profilo
  const [profileData, setProfileData] = useState(createDefaultProfileData());

  // Fatturazione
  const [billingData, setBillingData] = useState({
    companyName: '',
    vatNumber: '',
    address: '',
    city: '',
    postcode: '',
    country: ''
  });
  const [integrationsData, setIntegrationsData] = useState({
    google_drive: { enabled: false, folderId: '', folderName: '' },
    mega: { enabled: false, folderPath: '', folderName: '' }
  });
  const [appPreferences, setAppPreferences] = useState({
    enableSeparateSingleCableSiglature: false
  });
  const [cableCatalog, setCableCatalog] = useState(getDefaultCableCatalog());
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogModal, setCatalogModal] = useState({
    isOpen: false,
    type: 'unipolari',
    mode: 'add',
    item: createUnipolarCableItem()
  });
  const [catalogError, setCatalogError] = useState('');
  const [catalogSectionError, setCatalogSectionError] = useState('');
  const [machineSettings, setMachineSettings] = useState({
    enabled: {
      'EW-3035': true,
      'EW-05': false
    },
    machines: {
      'EW-3035': { cable_sections: {}, advanced_settings: EW3035_ADVANCED_SETTINGS_DEFAULTS },
      'EW-05': {}
    }
  });

  // Terminali e Sezioni
  const [localTerminals, setLocalTerminals] = useState({});
  const [terminalSearch, setTerminalSearch] = useState('');
  const [terminalModal, setTerminalModal] = useState({ isOpen: false, code: '', draft: null });
  const [terminalModalError, setTerminalModalError] = useState('');
  const [localSections, setLocalSections] = useState({});
  const availableAWGSections = React.useMemo(
    () => getConfiguredAWGSections(localSections.SEZIONI_AWG || SEZIONI_AWG),
    [localSections.SEZIONI_AWG]
  );
  const filteredCableCatalog = React.useMemo(() => ({
    unipolari: (cableCatalog.unipolari || []).filter((item) => matchesCatalogSearch(item, catalogSearch)),
    multipolari: (cableCatalog.multipolari || []).filter((item) => matchesCatalogSearch(item, catalogSearch))
  }), [cableCatalog, catalogSearch]);

  React.useEffect(() => {
    if (settings) {
      setLocalTerminals(settings.terminal_data || {});
      setLocalSections(settings.cable_sections || {});
      setMachineSettings({
        enabled: {
          'EW-3035': settings.machine_settings?.enabled?.['EW-3035'] ?? true,
          'EW-05': settings.machine_settings?.enabled?.['EW-05'] ?? false
        },
        machines: {
          'EW-3035': {
            ...(settings.machine_settings?.machines?.['EW-3035'] || {}),
            cable_sections: settings.machine_settings?.machines?.['EW-3035']?.cable_sections || settings.cable_sections || {},
            advanced_settings: settings.machine_settings?.machines?.['EW-3035']?.advanced_settings || EW3035_ADVANCED_SETTINGS_DEFAULTS
          },
          'EW-05': settings.machine_settings?.machines?.['EW-05'] || {}
        }
      });
      setProfileData({
        ...createDefaultProfileData(),
        ...(settings.profile_data || {})
      });
      setBillingData(settings.billing_data || {});
      setAppPreferences({
        enableSeparateSingleCableSiglature: !!settings.default_settings?.app_settings?.enableSeparateSingleCableSiglature
      });
      setCableCatalog({
        ...getDefaultCableCatalog(),
        ...(settings.default_settings?.cable_catalog || {})
      });
      setIntegrationsData({
        google_drive: {
          enabled: !!settings.integrations?.google_drive?.enabled,
          folderId: settings.integrations?.google_drive?.folderId || '',
          folderName: settings.integrations?.google_drive?.folderName || ''
        },
        mega: {
          enabled: !!settings.integrations?.mega?.enabled,
          folderPath: settings.integrations?.mega?.folderPath || '',
          folderName: settings.integrations?.mega?.folderName || ''
        }
      });
    }
  }, [settings]);

  React.useEffect(() => {
    let isActive = true;
    let nextObjectUrl = '';

    const loadLogoPreview = async () => {
      if (!profileData.logo?.relativePath) {
        setLogoPreviewUrl((previousUrl) => {
          if (previousUrl) {
            URL.revokeObjectURL(previousUrl);
          }
          return '';
        });
        return;
      }

      try {
        const previewUrl = await loadCurrentUserLogoPreviewUrl();
        if (!isActive) {
          URL.revokeObjectURL(previewUrl);
          return;
        }

        nextObjectUrl = previewUrl;
        setLogoPreviewUrl((previousUrl) => {
          if (previousUrl) {
            URL.revokeObjectURL(previousUrl);
          }
          return previewUrl;
        });
      } catch (error) {
        if (isActive) {
          setLogoPreviewUrl((previousUrl) => {
            if (previousUrl) {
              URL.revokeObjectURL(previousUrl);
            }
            return '';
          });
        }
      }
    };

    loadLogoPreview();

    return () => {
      isActive = false;
      if (nextObjectUrl) {
        URL.revokeObjectURL(nextObjectUrl);
      }
    };
  }, [profileData.logo?.relativePath, profileData.logo?.uploadedAt]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      const result = await saveSettings({
        ...settings,
        terminal_data: localTerminals,
        cable_sections: localSections,
        machine_settings: {
          ...machineSettings,
          machines: {
            ...machineSettings.machines,
            'EW-3035': {
              ...(machineSettings.machines?.['EW-3035'] || {}),
              cable_sections: localSections,
              advanced_settings: omitProjectSettingKeys(machineSettings.machines?.['EW-3035']?.advanced_settings || EW3035_ADVANCED_SETTINGS_DEFAULTS)
            }
          }
        },
        default_settings: {
          ...(settings.default_settings || {}),
          app_settings: {
            ...(settings.default_settings?.app_settings || {}),
            enableSeparateSingleCableSiglature: !!appPreferences.enableSeparateSingleCableSiglature
          },
          cable_catalog: cableCatalog
        },
        profile_data: profileData,
        billing_data: billingData,
        integrations: integrationsData
      });
      
      if (result.success) {
        setSaveMessageType('success');
        setSaveMessage(t('userSettingsPage.saveSuccess'));
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setSaveMessageType('error');
        setSaveMessage(t('userSettingsPage.saveErrorWithReason', { error: result.error }));
      }
    } catch (error) {
      setSaveMessageType('error');
      setSaveMessage(t('userSettingsPage.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenLogoPicker = () => {
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
      logoInputRef.current.click();
    }
  };

  const handleLogoSelected = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setIsUploadingLogo(true);
    setLogoMessage('');

    try {
      const payload = await prepareLogoUploadPayload(selectedFile);
      const result = await settingsAPI.uploadLogo(payload);
      const nextProfileData = {
        ...profileData,
        logo: result.logo
      };

      setProfileData(nextProfileData);
      updateSettings({
        profile_data: nextProfileData
      });

      setLogoMessageType('success');
      setLogoMessage(t('userSettingsPage.profile.logoUploadSuccess'));
    } catch (error) {
      setLogoMessageType('error');
      setLogoMessage(error.message || t('userSettingsPage.profile.logoUploadError'));
    } finally {
      setIsUploadingLogo(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!profileData.logo) return;

    setIsRemovingLogo(true);
    setLogoMessage('');

    try {
      await settingsAPI.removeLogo();
      const nextProfileData = {
        ...profileData,
        logo: null
      };

      setProfileData(nextProfileData);
      updateSettings({
        profile_data: nextProfileData
      });

      setLogoPreviewUrl((previousUrl) => {
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        return '';
      });
      setLogoMessageType('success');
      setLogoMessage(t('userSettingsPage.profile.logoRemoveSuccess'));
    } catch (error) {
      setLogoMessageType('error');
      setLogoMessage(error.message || t('userSettingsPage.profile.logoRemoveError'));
    } finally {
      setIsRemovingLogo(false);
    }
  };

  const toTerminalInputValue = (value) => (value === null || value === undefined ? '' : String(value));

  const parseTerminalNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Le sezioni possono essere assenti (terminale senza limiti): la stringa vuota
  // resta null, non 0, altrimenti il terminale risulterebbe incompatibile con tutto.
  const parseTerminalSection = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const openTerminalModal = (terminalCode) => {
    const terminal = localTerminals[terminalCode] || {};
    setTerminalModalError('');
    setTerminalModal({
      isOpen: true,
      code: terminalCode,
      draft: {
        nome: terminal.nome || '',
        minSection: toTerminalInputValue(terminal.minSection),
        maxSection: toTerminalInputValue(terminal.maxSection),
        allowsMultiple: terminal.allowsMultiple === true,
        ...TERMINAL_NUMERIC_FIELDS.reduce((fields, field) => {
          fields[field] = toTerminalInputValue(terminal[field] ?? 0);
          return fields;
        }, {})
      }
    });
  };

  const closeTerminalModal = () => {
    setTerminalModalError('');
    setTerminalModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleTerminalModalChange = (field, value) => {
    setTerminalModalError('');
    setTerminalModal(prev => ({ ...prev, draft: { ...prev.draft, [field]: value } }));
  };

  const handleSaveTerminalModal = () => {
    const { code, draft } = terminalModal;
    if (!code || !draft) return;

    const minSection = parseTerminalSection(draft.minSection);
    const maxSection = parseTerminalSection(draft.maxSection);

    if (minSection !== null && maxSection !== null && minSection > maxSection) {
      setTerminalModalError(t('userSettingsPage.terminals.sectionRangeError'));
      return;
    }

    setLocalTerminals(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        nome: draft.nome.trim() || prev[code]?.nome || code,
        minSection,
        maxSection,
        allowsMultiple: draft.allowsMultiple === true,
        ...TERMINAL_NUMERIC_FIELDS.reduce((fields, field) => {
          fields[field] = parseTerminalNumber(draft[field]);
          return fields;
        }, {})
      }
    }));

    closeTerminalModal();
  };

  const handleResetTerminal = (terminalCode) => {
    const defaultTerminal = TERMINAL_DATA[terminalCode];
    if (!defaultTerminal) return;

    setLocalTerminals(prev => ({ ...prev, [terminalCode]: { ...defaultTerminal } }));
    if (terminalModal.isOpen && terminalModal.code === terminalCode) {
      closeTerminalModal();
    }
  };

  const handleResetAllTerminals = () => {
    if (!window.confirm(t('userSettingsPage.terminals.resetAllConfirm'))) return;

    setLocalTerminals(prev => {
      const restored = { ...prev };
      Object.entries(TERMINAL_DATA).forEach(([code, defaultTerminal]) => {
        restored[code] = { ...defaultTerminal };
      });
      return restored;
    });
  };

  const handleIconToggle = (terminalCode) => {
    setLocalTerminals(prev => {
      if (!terminalSupportsIconVariants(terminalCode)) {
        return prev;
      }

      const currentTerminal = prev[terminalCode] || {};
      const currentVariant = currentTerminal.iconVariant || 'default';
      
      if (currentTerminal.hasOwnProperty('iconVariant')) {
        return {
          ...prev,
          [terminalCode]: {
            ...currentTerminal,
            iconVariant: currentVariant === 'default' ? 'alt' : 'default'
          }
        };
      }

      return prev;
    });
  };

  const handleSectionChange = (sectionType, index, field, value) => {
    setLocalSections(prev => ({
      ...prev,
      [sectionType]: prev[sectionType].map((sec, idx) => 
        idx === index ? { ...sec, [field]: value === '' ? '' : parseFloat(value) || 0 } : sec
      )
    }));
  };

  const ew3035AdvancedSettings = {
    ...EW3035_ADVANCED_SETTINGS_DEFAULTS,
    ...(machineSettings.machines?.['EW-3035']?.advanced_settings || {}),
    compensazioneMarcaturaPerWordSize: {
      ...EW3035_ADVANCED_SETTINGS_DEFAULTS.compensazioneMarcaturaPerWordSize,
      ...(machineSettings.machines?.['EW-3035']?.advanced_settings?.compensazioneMarcaturaPerWordSize || {})
    }
  };

  const setEW3035AdvancedSettings = (nextSettings) => {
    setMachineSettings(prev => {
      const currentMachine = prev.machines?.['EW-3035'] || {};
      const currentAdvanced = {
        ...EW3035_ADVANCED_SETTINGS_DEFAULTS,
        ...(currentMachine.advanced_settings || {}),
        compensazioneMarcaturaPerWordSize: {
          ...EW3035_ADVANCED_SETTINGS_DEFAULTS.compensazioneMarcaturaPerWordSize,
          ...(currentMachine.advanced_settings?.compensazioneMarcaturaPerWordSize || {})
        }
      };
      const resolvedAdvanced = typeof nextSettings === 'function'
        ? nextSettings(currentAdvanced)
        : nextSettings;

      return {
        ...prev,
        machines: {
          ...prev.machines,
          'EW-3035': {
            ...currentMachine,
            advanced_settings: omitProjectSettingKeys({
              ...EW3035_ADVANCED_SETTINGS_DEFAULTS,
              ...resolvedAdvanced
            })
          }
        }
      };
    });
  };

  const handleMachineToggle = (machineId, enabled) => {
    if (!enabled && activeSection === `machine-${machineId.toLowerCase().replace('-', '')}`) {
      setActiveSection('machines');
    }

    setMachineSettings(prev => ({
      ...prev,
      enabled: {
        ...prev.enabled,
        [machineId]: enabled
      },
      machines: {
        ...prev.machines,
        [machineId]: prev.machines?.[machineId] || {}
      }
    }));
  };

  const handleIntegrationChange = (provider, field, value) => {
    setIntegrationsData(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
  };

  const handleFolderSelected = (provider, folder) => {
    if (provider === 'google_drive') {
      setIntegrationsData(prev => ({
        ...prev,
        google_drive: {
          ...prev.google_drive,
          folderId: folder.id,
          folderName: folder.name
        }
      }));
      return;
    }

    setIntegrationsData(prev => ({
      ...prev,
      mega: {
        ...prev.mega,
        folderPath: folder.path,
        folderName: folder.name
      }
    }));
  };

  const openCatalogModal = (catalogType, mode, item = null) => {
    const colorNormalizedItem = item ? normalizeCatalogItemColors(item, catalogType) : null;
    const baseItem = colorNormalizedItem
      ? {
          ...colorNormalizedItem,
          standardSezione: getCatalogSectionStandard(colorNormalizedItem),
          ...(getCatalogSectionStandard(colorNormalizedItem) === 'awg' && !colorNormalizedItem.calibroAWG
            ? { calibroAWG: findCatalogAWGSectionByMetric(colorNormalizedItem.sezione, availableAWGSections)?.val || '' }
            : {})
        }
      : (catalogType === 'unipolari' ? createUnipolarCableItem() : createMultipolarCableItem());

    setCatalogError('');
    setCatalogSectionError('');
    setCatalogModal({
      isOpen: true,
      type: catalogType,
      mode,
      item: catalogType === 'multipolari'
        ? {
            ...baseItem,
            numeroPoli: normalizePoleCount(baseItem.numeroPoli ?? 0),
            poliColori: syncPoleColorsLength(baseItem.poliColori || [], baseItem.numeroPoli ?? 0)
          }
        : baseItem
    });
  };

  const closeCatalogModal = () => {
    setCatalogError('');
    setCatalogSectionError('');
    setCatalogModal(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  const handleCatalogModalChange = (field, value) => {
    if (field === 'codiceInterno') setCatalogError('');
    if (['standardSezione', 'sezione', 'calibroAWG'].includes(field)) setCatalogSectionError('');
    setCatalogModal(prev => {
      const nextItem = { ...prev.item };

      if (field === 'standardSezione') {
        Object.assign(nextItem, changeCatalogSectionStandard(nextItem, value, availableAWGSections));
        if (value !== 'awg') delete nextItem.calibroAWG;
      } else if (field === 'calibroAWG') {
        Object.assign(nextItem, changeCatalogAWGGauge(nextItem, value, availableAWGSections));
      } else if (prev.type === 'multipolari' && field === 'numeroPoli') {
        const nextPoleCount = normalizePoleCount(value);
        nextItem.numeroPoli = nextPoleCount;
        nextItem.poliColori = syncPoleColorsLength(nextItem.poliColori || [], nextPoleCount);
      } else {
        nextItem[field] = value;
      }

      return {
        ...prev,
        item: nextItem
      };
    });
  };

  const handleRemoveCatalogItem = (catalogType, itemId) => {
    setCableCatalog(prev => ({
      ...prev,
      [catalogType]: prev[catalogType].filter(item => item.id !== itemId)
    }));
  };

  const handleSaveCatalogModal = () => {
    const colorNormalizedItem = normalizeCatalogItemColors(catalogModal.item, catalogModal.type);
    const normalizedItem = catalogModal.type === 'multipolari'
      ? {
          ...colorNormalizedItem,
          numeroPoli: normalizePoleCount(colorNormalizedItem.numeroPoli ?? 0),
          poliColori: syncPoleColorsLength(
            colorNormalizedItem.poliColori || [],
            colorNormalizedItem.numeroPoli ?? 0
          )
        }
      : colorNormalizedItem;

    const sectionValidation = normalizeCatalogItemSection(normalizedItem, availableAWGSections);
    if (!sectionValidation.isValid) {
      setCatalogSectionError(t(sectionValidation.reason === 'awg-required'
        ? 'userSettingsPage.cableCatalog.awgRequired'
        : 'userSettingsPage.cableCatalog.sectionRequired'));
      return;
    }
    Object.assign(normalizedItem, sectionValidation.item);

    const validation = validateCatalogItem(cableCatalog, normalizedItem);
    if (!validation.isValid) {
      setCatalogError(t(`userSettingsPage.cableCatalog.${validation.reason === 'duplicate' ? 'duplicateInternalCode' : 'internalCodeRequired'}`));
      return;
    }
    normalizedItem.codiceInterno = validation.codiceInterno;
    normalizedItem.descrizione = normalizeCableDescription(normalizedItem.descrizione);

    setCableCatalog(prev => {
      const nextItems = catalogModal.mode === 'edit'
        ? prev[catalogModal.type].map((item) => item.id === normalizedItem.id ? normalizedItem : item)
        : [...prev[catalogModal.type], normalizedItem];

      return {
        ...prev,
        [catalogModal.type]: nextItems
      };
    });

    closeCatalogModal();
  };

  const handleAddPoleColor = () => {
    setCatalogModal(prev => {
      const nextPoleColors = [...(prev.item.poliColori || []), ''];
      return {
        ...prev,
        item: {
          ...prev.item,
          numeroPoli: nextPoleColors.length,
          poliColori: nextPoleColors
        }
      };
    });
  };

  const handlePoleColorChange = (poleIndex, value) => {
    setCatalogModal(prev => {
      const nextPoleColors = [...(prev.item.poliColori || [])];
      nextPoleColors[poleIndex] = value;

      return {
        ...prev,
        item: {
          ...prev.item,
          poliColori: nextPoleColors
        }
      };
    });
  };

  const handleRemovePoleColor = (poleIndex) => {
    setCatalogModal(prev => {
      const nextPoleColors = (prev.item.poliColori || []).filter((_, index) => index !== poleIndex);
      return {
        ...prev,
        item: {
          ...prev.item,
          numeroPoli: nextPoleColors.length,
          poliColori: nextPoleColors
        }
      };
    });
  };

  const cableColors = React.useMemo(
    () => getColorOptionsForLanguage(language).map(({ value }) => value),
    [language]
  );

  const groupedTerminals = {};
  Object.entries(TERMINAL_GROUPS).forEach(([group, terminals]) => {
    terminals.forEach(code => {
      if (localTerminals[code]) {
        if (!groupedTerminals[group]) groupedTerminals[group] = [];
        groupedTerminals[group].push(code);
      }
    });
  });

  const getTerminalDisplayName = (code) => {
    const translationKey = `terminals.${code.replace(/\./g, '_')}`;
    const translated = t(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }

    return localTerminals[code]?.nome || code;
  };

  const matchesTerminalSearch = (code) => {
    const query = terminalSearch.trim().toLowerCase();
    if (!query) return true;

    const name = getTerminalDisplayName(code).toLowerCase();
    return code.toLowerCase().includes(query) || name.includes(query);
  };

  const filteredGroupedTerminals = Object.entries(groupedTerminals).reduce((groups, [group, codes]) => {
    const matching = codes.filter(matchesTerminalSearch);
    if (matching.length > 0) groups[group] = matching;
    return groups;
  }, {});

  const filteredTerminalsCount = Object.values(filteredGroupedTerminals).reduce((total, codes) => total + codes.length, 0);
  const customizedTerminalsCount = Object.entries(localTerminals)
    .filter(([code, terminal]) => isTerminalCustomized(code, terminal)).length;

  const formatTerminalSectionRange = (terminal) => {
    const { minSection, maxSection } = terminal || {};
    if (minSection == null && maxSection == null) return t('userSettingsPage.terminals.unbounded');
    if (minSection == null) return `≤ ${maxSection}`;
    if (maxSection == null) return `≥ ${minSection}`;
    return minSection === maxSection ? `${minSection}` : `${minSection} – ${maxSection}`;
  };

  const sections = [
    { id: 'profile', label: t('userSettingsPage.sections.profile'), icon: Icons.User },
    { id: 'app-preferences', label: t('userSettingsPage.sections.appPreferences'), icon: Icons.Settings },
    { id: 'cables', label: t('userSettingsPage.sections.cables'), icon: Icons.Cable },
    { id: 'billing', label: t('userSettingsPage.sections.billing'), icon: Icons.FileSpreadsheet },
    { id: 'integrations', label: t('userSettingsPage.sections.integrations'), icon: Icons.Settings },
    { id: 'machines', label: t('userSettingsPage.sections.machines'), icon: Icons.Cable },
    { id: 'terminals', label: t('userSettingsPage.sections.terminals'), icon: Icons.Wire },
  ];
  const availableMachines = getMachineList();
  const enabledMachineSections = [
    { id: 'machine-ew3035', machineId: 'EW-3035', label: 'EW-3035+L' },
    { id: 'machine-ew05', machineId: 'EW-05', label: 'EW-05' }
  ].filter(machine => machineSettings.enabled?.[machine.machineId]);

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-black shadow-lg py-4 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <Icons.X className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-white">{t('common.settings')}</h1>
        </div>
        <div className="flex items-center space-x-4">
          {saveMessage && (
            <span className={`text-sm ${saveMessageType === 'error' ? 'text-red-400' : 'text-green-400'}`}>
              {saveMessage}
            </span>
          )}
          <LanguageToggle />
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? t('userSettingsPage.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <nav className="py-4">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center space-x-3 px-6 py-3 transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <section.icon className="w-5 h-5" />
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
            {enabledMachineSections.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="px-6 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('userSettingsPage.machineGroup')}
                </div>
                {enabledMachineSections.map(machine => (
                  <button
                    key={machine.id}
                    onClick={() => setActiveSection(machine.id)}
                    className={`w-full flex items-center pl-10 pr-6 py-2 text-sm transition-colors ${
                      activeSection === machine.id
                        ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{machine.label}</span>
                  </button>
                ))}
              </div>
            )}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="max-w-3xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('userSettingsPage.profile.title')}</h2>
              
              <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={LOGO_ACCEPT_ATTRIBUTE}
                  onChange={handleLogoSelected}
                  className="hidden"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.profile.username')}
                  </label>
                  <input
                    type="text"
                    value={profileData.username}
                    onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.profile.email')}
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t('userSettingsPage.profile.logoTitle')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('userSettingsPage.profile.logoDescription')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('userSettingsPage.profile.logoFormats')}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleOpenLogoPicker}
                        disabled={isUploadingLogo}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {isUploadingLogo
                          ? t('userSettingsPage.profile.logoUploading')
                          : profileData.logo
                            ? t('userSettingsPage.profile.logoReplace')
                            : t('userSettingsPage.profile.logoUpload')}
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        disabled={!profileData.logo || isRemovingLogo}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                      >
                        {isRemovingLogo
                          ? t('userSettingsPage.profile.logoRemoving')
                          : t('userSettingsPage.profile.logoRemove')}
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-4 md:grid-cols-[140px,1fr]">
                    <div className="flex h-[120px] w-[120px] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white p-3">
                      {logoPreviewUrl ? (
                        <img
                          src={logoPreviewUrl}
                          alt={t('userSettingsPage.profile.logoPreviewAlt')}
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <span className="text-center text-xs text-gray-400">
                          {t('userSettingsPage.profile.logoNoPreview')}
                        </span>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            {t('userSettingsPage.profile.logoFileName')}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-gray-900 break-all">
                            {profileData.logo?.fileName || '—'}
                          </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            {t('userSettingsPage.profile.logoMimeType')}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-gray-900">
                            {profileData.logo?.mimeType || '—'}
                          </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            {t('userSettingsPage.profile.logoDimensions')}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-gray-900">
                            {profileData.logo?.width && profileData.logo?.height
                              ? `${profileData.logo.width} × ${profileData.logo.height}px`
                              : '—'}
                          </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            {t('userSettingsPage.profile.logoUpdatedAt')}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-gray-900">
                            {formatLogoTimestamp(profileData.logo?.uploadedAt, language === 'it' ? 'it-IT' : 'en-US')}
                          </div>
                        </div>
                      </div>

                      {logoMessage && (
                        <div className={`rounded-md px-4 py-3 text-sm ${
                          logoMessageType === 'error'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                        }`}>
                          {logoMessage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <hr className="my-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('userSettingsPage.profile.passwordChange')}</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.profile.currentPassword')}
                  </label>
                  <input
                    type="password"
                    value={profileData.currentPassword}
                    onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.profile.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={profileData.newPassword}
                    onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.profile.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    value={profileData.confirmPassword}
                    onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'app-preferences' && (
            <div className="max-w-3xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('userSettingsPage.appPreferences.title')}</h2>

              <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{t('userSettingsPage.appPreferences.cableWorkspaceTitle')}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('userSettingsPage.appPreferences.cableWorkspaceDesc')}
                  </p>
                </div>

                <label className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 cursor-pointer">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {t('userSettingsPage.appPreferences.separateSingleCableSiglature')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('userSettingsPage.appPreferences.separateSingleCableSiglatureDesc')}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={!!appPreferences.enableSeparateSingleCableSiglature}
                    onChange={(e) => setAppPreferences(prev => ({
                      ...prev,
                      enableSeparateSingleCableSiglature: e.target.checked
                    }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              </div>
            </div>
          )}

          {activeSection === 'cables' && (
            <div className="max-w-6xl mx-auto space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{t('userSettingsPage.cableCatalog.title')}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('userSettingsPage.cableCatalog.desc')}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-4">
                <label htmlFor="cable-catalog-search" className="sr-only">
                  {t('userSettingsPage.cableCatalog.searchLabel')}
                </label>
                <div className="relative">
                  <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    id="cable-catalog-search"
                    type="search"
                    value={catalogSearch}
                    onChange={(event) => setCatalogSearch(event.target.value)}
                    placeholder={t('userSettingsPage.cableCatalog.searchPlaceholder')}
                    className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-10 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  {catalogSearch && (
                    <button
                      type="button"
                      onClick={() => setCatalogSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      title={t('userSettingsPage.cableCatalog.clearSearch')}
                      aria-label={t('userSettingsPage.cableCatalog.clearSearch')}
                    >
                      <Icons.X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {catalogSearch.trim() && (
                  <div className="mt-2 text-xs text-gray-500">
                    {filteredCableCatalog.unipolari.length + filteredCableCatalog.multipolari.length}{' '}
                    {t('userSettingsPage.cableCatalog.searchResults')}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{t('userSettingsPage.cableCatalog.unipolari')}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => openCatalogModal('unipolari', 'add')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    {t('userSettingsPage.cableCatalog.addUnipolar')}
                  </button>
                </div>

                {cableCatalog.unipolari.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    {t('userSettingsPage.cableCatalog.noUnipolar')}
                  </div>
                ) : filteredCableCatalog.unipolari.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    {t('userSettingsPage.cableCatalog.noSearchResults')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCableCatalog.unipolari.map((item) => (
                      <CableCatalogItemCard
                        key={item.id}
                        item={item}
                        actions={(
                          <>
                            <button
                              type="button"
                              onClick={() => openCatalogModal('unipolari', 'edit', item)}
                              className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                              title={t('common.edit')}
                            >
                              <Icons.Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveCatalogItem('unipolari', item.id)}
                              className="p-2 text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                              title={t('common.delete')}
                            >
                              <Icons.Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{t('userSettingsPage.cableCatalog.multipolari')}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => openCatalogModal('multipolari', 'add')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    {t('userSettingsPage.cableCatalog.addMultipolar')}
                  </button>
                </div>

                {cableCatalog.multipolari.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    {t('userSettingsPage.cableCatalog.noMultipolar')}
                  </div>
                ) : filteredCableCatalog.multipolari.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    {t('userSettingsPage.cableCatalog.noSearchResults')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCableCatalog.multipolari.map((item) => (
                      <CableCatalogItemCard
                        key={item.id}
                        item={item}
                        isMultipolar
                        actions={(
                          <>
                            <button
                              type="button"
                              onClick={() => openCatalogModal('multipolari', 'edit', item)}
                              className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                              title={t('common.edit')}
                            >
                              <Icons.Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveCatalogItem('multipolari', item.id)}
                              className="p-2 text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                              title={t('common.delete')}
                            >
                              <Icons.Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {catalogModal.isOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={closeCatalogModal}
            >
              <div
                className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center gap-4 mb-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-11 w-16 flex-shrink-0 rounded-lg border border-blue-200 bg-blue-50 p-1 flex items-center justify-center">
                      <CableTypeIcon
                        isMultipolar={catalogModal.type === 'multipolari'}
                        color={catalogModal.item.colore}
                        jacketColor={catalogModal.item.coloreGuaina}
                        poleColors={catalogModal.item.poliColori || []}
                        poleCount={catalogModal.item.numeroPoli}
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {catalogModal.mode === 'edit' ? t('common.edit') : t('common.add')} {' '}
                      {catalogModal.type === 'unipolari'
                        ? t('userSettingsPage.cableCatalog.unipolari')
                        : t('userSettingsPage.cableCatalog.multipolari')}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={closeCatalogModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icons.X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${catalogModal.type === 'unipolari' ? 'xl:grid-cols-3' : 'xl:grid-cols-2'} gap-4`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('userSettingsPage.cableCatalog.manufacturer')}
                      </label>
                      <input
                        type="text"
                        value={catalogModal.item.produttore || ''}
                        onChange={(e) => handleCatalogModalChange('produttore', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                    </div>

                    {catalogModal.type === 'unipolari' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('userSettingsPage.cableCatalog.color')}
                        </label>
                        <select
                          value={catalogModal.item.colore || ''}
                          onChange={(e) => handleCatalogModalChange('colore', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value=""></option>
                          {cableColors.map((color) => (
                            <option key={color} value={color}>{translateColorForDisplay(color, language)}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('userSettingsPage.cableCatalog.sectionStandard')}
                      </label>
                      <select
                        value={getCatalogSectionStandard(catalogModal.item)}
                        onChange={(e) => handleCatalogModalChange('standardSezione', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="metric">{t('userSettingsPage.cableCatalog.metricStandard')}</option>
                        <option value="awg">{t('userSettingsPage.cableCatalog.awgStandard')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getCatalogSectionStandard(catalogModal.item) === 'awg'
                          ? t('userSettingsPage.cableCatalog.awgGauge')
                          : t('userSettingsPage.cableCatalog.section')}
                      </label>
                      {getCatalogSectionStandard(catalogModal.item) === 'awg' ? (
                        <select
                          value={catalogModal.item.calibroAWG ?? ''}
                          onChange={(e) => handleCatalogModalChange('calibroAWG', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">{t('userSettingsPage.cableCatalog.selectAWG')}</option>
                          {availableAWGSections.map((section) => (
                            <option key={`${section.val}-${section.metric}`} value={section.val}>
                              {section.val} AWG · {section.metric} mm²
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={catalogModal.item.sezione ?? ''}
                          onChange={(e) => handleCatalogModalChange('sezione', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                      {catalogSectionError && (
                        <p className="mt-1 text-xs text-red-600">{catalogSectionError}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('userSettingsPage.cableCatalog.manufacturerCode')}
                      </label>
                      <input
                        type="text"
                        value={catalogModal.item.codiceCostruttore || ''}
                        onChange={(e) => handleCatalogModalChange('codiceCostruttore', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('userSettingsPage.cableCatalog.internalCode')} <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={catalogModal.item.codiceInterno || ''}
                        onChange={(e) => handleCatalogModalChange('codiceInterno', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                      {catalogError && (
                        <p className="mt-1 text-xs text-red-600">{catalogError}</p>
                      )}
                    </div>

                    <div className={catalogModal.type === 'unipolari' ? 'md:col-span-2 xl:col-span-3' : 'md:col-span-2'}>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('userSettingsPage.cableCatalog.description')}
                          <span className="text-xs text-gray-500 ml-1">{t('cableForm.optional')}</span>
                        </label>
                        <span className="text-xs text-gray-400">{(catalogModal.item.descrizione || '').length}/160</span>
                      </div>
                      <input
                        type="text"
                        maxLength={160}
                        value={catalogModal.item.descrizione || ''}
                        onChange={(e) => handleCatalogModalChange('descrizione', e.target.value)}
                        placeholder={t('userSettingsPage.cableCatalog.descriptionPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {catalogModal.type === 'multipolari' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('userSettingsPage.cableCatalog.poles')}
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={catalogModal.item.numeroPoli ?? 0}
                          onChange={(e) => handleCatalogModalChange('numeroPoli', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}

                    {catalogModal.type === 'multipolari' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('userSettingsPage.cableCatalog.outerSheathColor')}
                        </label>
                        <select
                          value={catalogModal.item.coloreGuaina || ''}
                          onChange={(e) => handleCatalogModalChange('coloreGuaina', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">{t('userSettingsPage.cableCatalog.defaultSheathColor')}</option>
                          {cableColors.map((color) => (
                            <option key={`jacket-${color}`} value={color}>{translateColorForDisplay(color, language)}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {catalogModal.type === 'multipolari' && (
                    <div className="space-y-3 pt-2">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-gray-800">{t('userSettingsPage.cableCatalog.poleColors')}</div>
                          <div className="text-xs text-gray-500 mt-1">{t('userSettingsPage.cableCatalog.poleColorsHelp')}</div>
                        </div>
                        <button
                          type="button"
                          onClick={handleAddPoleColor}
                          className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          {t('userSettingsPage.cableCatalog.addPoleColor')}
                        </button>
                      </div>

                      {(catalogModal.item.poliColori || []).length === 0 ? (
                        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-sm text-gray-500">
                          {t('userSettingsPage.cableCatalog.poleColors')}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                          {(catalogModal.item.poliColori || []).map((poleColor, poleIndex) => (
                            <div key={`${catalogModal.item.id}-pole-${poleIndex}`} className="flex items-center gap-2">
                              <select
                                value={poleColor || ''}
                                onChange={(e) => handlePoleColorChange(poleIndex, e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                              >
                                <option value=""></option>
                                {cableColors.map((color) => (
                                  <option key={`${catalogModal.item.id}-${color}-${poleIndex}`} value={color}>{translateColorForDisplay(color, language)}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => handleRemovePoleColor(poleIndex)}
                                className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                                title={t('userSettingsPage.cableCatalog.removePoleColor')}
                              >
                                <Icons.Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleSaveCatalogModal}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      type="button"
                      onClick={closeCatalogModal}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Section */}
          {activeSection === 'billing' && (
            <div className="max-w-3xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('userSettingsPage.billing.title')}</h2>
              
              <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.billing.companyName')}
                  </label>
                  <input
                    type="text"
                    value={billingData.companyName}
                    onChange={(e) => setBillingData({...billingData, companyName: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.billing.vatNumber')}
                  </label>
                  <input
                    type="text"
                    value={billingData.vatNumber}
                    onChange={(e) => setBillingData({...billingData, vatNumber: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.billing.address')}
                  </label>
                  <input
                    type="text"
                    value={billingData.address}
                    onChange={(e) => setBillingData({...billingData, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('userSettingsPage.billing.city')}
                    </label>
                    <input
                      type="text"
                      value={billingData.city}
                      onChange={(e) => setBillingData({...billingData, city: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('userSettingsPage.billing.postcode')}
                    </label>
                    <input
                      type="text"
                      value={billingData.postcode}
                      onChange={(e) => setBillingData({...billingData, postcode: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userSettingsPage.billing.country')}
                  </label>
                  <input
                    type="text"
                    value={billingData.country}
                    onChange={(e) => setBillingData({...billingData, country: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'integrations' && (
            <div className="max-w-3xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('userSettingsPage.integrations.title')}</h2>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Google Drive</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('userSettingsPage.integrations.googleDriveDesc')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    integrationsData.google_drive.enabled && isGoogleDriveConfigured()
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {integrationsData.google_drive.enabled && isGoogleDriveConfigured() ? t('userSettingsPage.integrations.enabled') : t('userSettingsPage.integrations.needsSetup')}
                  </span>
                </div>

                <div className="mt-5 border-t border-gray-100 pt-4 space-y-4">
                  <label className="flex items-center gap-3 text-sm font-medium text-gray-800">
                    <input
                      type="checkbox"
                      checked={integrationsData.google_drive.enabled}
                      onChange={(e) => handleIntegrationChange('google_drive', 'enabled', e.target.checked)}
                      className="w-4 h-4"
                    />
                    {t('userSettingsPage.integrations.enableGoogleDrive')}
                  </label>
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-700">{t('userSettingsPage.integrations.googleDriveFolder')}</div>
                        <div className="text-sm text-gray-900 font-semibold truncate mt-1">
                          {integrationsData.google_drive.folderName || t('userSettingsPage.integrations.noFolderSelected')}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFolderPickerProvider('google_drive')}
                        disabled={!isGoogleDriveConfigured()}
                        className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('userSettingsPage.integrations.chooseFolder')}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('userSettingsPage.integrations.folderHelp')}
                    </p>
                  </div>
                </div>

                <div className="mt-5 text-sm text-gray-600 space-y-2">
                  <p>
                    {t('userSettingsPage.integrations.googleDriveSetupPrefix')} <code className="px-1 py-0.5 bg-gray-100 rounded">REACT_APP_GOOGLE_CLIENT_ID</code> {t('userSettingsPage.integrations.googleDriveSetupSuffix')} <code className="px-1 py-0.5 bg-gray-100 rounded">.env</code> {t('userSettingsPage.integrations.googleDriveSetupSuffix2')}
                  </p>
                  <p>
                    {t('userSettingsPage.integrations.googleDriveTokenNote')}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">MEGA</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('userSettingsPage.integrations.megaDesc')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    integrationsData.mega.enabled
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {integrationsData.mega.enabled ? t('userSettingsPage.integrations.enabled') : t('userSettingsPage.integrations.needsSetup')}
                  </span>
                </div>

                <div className="mt-5 border-t border-gray-100 pt-4 space-y-4">
                  <label className="flex items-center gap-3 text-sm font-medium text-gray-800">
                    <input
                      type="checkbox"
                      checked={integrationsData.mega.enabled}
                      onChange={(e) => handleIntegrationChange('mega', 'enabled', e.target.checked)}
                      className="w-4 h-4"
                    />
                    {t('userSettingsPage.integrations.enableMega')}
                  </label>
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-700">{t('userSettingsPage.integrations.megaFolder')}</div>
                        <div className="text-sm text-gray-900 font-semibold truncate mt-1">
                          {integrationsData.mega.folderPath || t('userSettingsPage.integrations.noFolderSelected')}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFolderPickerProvider('mega')}
                        className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-semibold hover:bg-gray-800"
                      >
                        {t('userSettingsPage.integrations.chooseFolder')}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('userSettingsPage.integrations.folderHelp')}
                    </p>
                  </div>
                </div>

                <div className="mt-5 text-sm text-gray-600 space-y-2">
                  <p>
                    {t('userSettingsPage.integrations.megaCredentialsNote')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Terminals Section */}
          {activeSection === 'terminals' && (
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">{t('userSettingsPage.terminals.title')}</h2>
                  <p className="text-sm text-gray-500 mt-1">{t('userSettingsPage.terminals.desc')}</p>
                </div>
                <button
                  type="button"
                  onClick={handleResetAllTerminals}
                  className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  {t('userSettingsPage.terminals.resetAll')}
                </button>
              </div>

              <div className="bg-white rounded-lg shadow-md p-4">
                <label htmlFor="terminal-search" className="sr-only">
                  {t('userSettingsPage.terminals.searchLabel')}
                </label>
                <div className="relative">
                  <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    id="terminal-search"
                    type="search"
                    value={terminalSearch}
                    onChange={(event) => setTerminalSearch(event.target.value)}
                    placeholder={t('userSettingsPage.terminals.searchPlaceholder')}
                    className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-10 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  {terminalSearch && (
                    <button
                      type="button"
                      onClick={() => setTerminalSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      title={t('userSettingsPage.terminals.clearSearch')}
                      aria-label={t('userSettingsPage.terminals.clearSearch')}
                    >
                      <Icons.X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                  {terminalSearch.trim() && (
                    <span>{filteredTerminalsCount} {t('userSettingsPage.terminals.searchResults')}</span>
                  )}
                  {customizedTerminalsCount > 0 && (
                    <span>{customizedTerminalsCount} {t('userSettingsPage.terminals.customizedCount')}</span>
                  )}
                </div>
              </div>

              {filteredTerminalsCount === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                  {t('userSettingsPage.terminals.noSearchResults')}
                </div>
              ) : (
                Object.entries(filteredGroupedTerminals).map(([group, terminals]) => (
                  <div key={group} className="bg-white rounded-lg shadow-md p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">{t(group)}</h3>

                    <div className="space-y-3">
                      {terminals.map(code => {
                        const terminal = localTerminals[code] || {};
                        const hasAlternative = terminalSupportsIconVariants(code);
                        const isCustomized = isTerminalCustomized(code, terminal);

                        return (
                          <div key={code} className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 hover:bg-gray-50 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                              <div
                                className={`w-14 h-7 flex items-center justify-center flex-shrink-0 ${hasAlternative ? 'cursor-pointer hover:ring-2 hover:ring-blue-400 rounded transition-all' : ''}`}
                                onClick={() => hasAlternative && handleIconToggle(code)}
                                title={hasAlternative ? t('userSettingsPage.terminals.toggleIcon') : ''}
                              >
                                {renderTerminalIconSimple(code, 56, 28, localTerminals)}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-800">{code}</span>
                                  {isCustomized && (
                                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                      {t('userSettingsPage.terminals.customized')}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 truncate">{getTerminalDisplayName(code)}</div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600 sm:grid-cols-4 lg:text-right">
                              <div>
                                <div className="text-gray-400">{t('userSettingsPage.terminals.sectionRange')}</div>
                                <div className="font-medium text-gray-800">{formatTerminalSectionRange(terminal)}</div>
                              </div>
                              <div>
                                <div className="text-gray-400">{t('userSettingsPage.terminals.len')} / {t('userSettingsPage.terminals.peel')}</div>
                                <div className="font-medium text-gray-800">{terminal.len ?? 0} / {terminal.peel ?? 0}</div>
                              </div>
                              <div>
                                <div className="text-gray-400">{t('userSettingsPage.terminals.printOffset')}</div>
                                <div className="font-medium text-gray-800">{terminal.printOffset ?? 0}</div>
                              </div>
                              <div>
                                <div className="text-gray-400">{t('userSettingsPage.terminals.allowsMultiple')}</div>
                                <div className="font-medium text-gray-800">
                                  {terminal.allowsMultiple ? t('common.yes') : t('common.no')}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 lg:flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => openTerminalModal(code)}
                                className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                                title={t('common.edit')}
                              >
                                <Icons.Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleResetTerminal(code)}
                                disabled={!isCustomized}
                                className="px-3 py-2 text-xs border border-gray-300 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                                title={t('userSettingsPage.terminals.reset')}
                              >
                                {t('userSettingsPage.terminals.reset')}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {terminalModal.isOpen && terminalModal.draft && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={closeTerminalModal}
            >
              <div
                className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center gap-4 mb-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-11 w-16 flex-shrink-0 rounded-lg border border-blue-200 bg-blue-50 p-1 flex items-center justify-center">
                      {renderTerminalIconSimple(terminalModal.code, 56, 28, localTerminals)}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {t('common.edit')} {terminalModal.code}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={closeTerminalModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icons.X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('userSettingsPage.terminals.name')}
                    </label>
                    <input
                      type="text"
                      value={terminalModal.draft.nome}
                      onChange={(e) => handleTerminalModalChange('nome', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                    <div>
                      <div className="text-sm font-medium text-gray-800">{t('userSettingsPage.terminals.compatibility')}</div>
                      <div className="text-xs text-gray-500 mt-1">{t('userSettingsPage.terminals.compatibilityHelp')}</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('userSettingsPage.terminals.minSection')}
                        </label>
                        <input
                          type="number"
                          step="0.05"
                          min="0"
                          value={terminalModal.draft.minSection}
                          onChange={(e) => handleTerminalModalChange('minSection', e.target.value)}
                          placeholder={t('userSettingsPage.terminals.unbounded')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('userSettingsPage.terminals.maxSection')}
                        </label>
                        <input
                          type="number"
                          step="0.05"
                          min="0"
                          value={terminalModal.draft.maxSection}
                          onChange={(e) => handleTerminalModalChange('maxSection', e.target.value)}
                          placeholder={t('userSettingsPage.terminals.unbounded')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                          <input
                            type="checkbox"
                            checked={terminalModal.draft.allowsMultiple}
                            onChange={(e) => handleTerminalModalChange('allowsMultiple', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          {t('userSettingsPage.terminals.allowsMultiple')}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                    <div>
                      <div className="text-sm font-medium text-gray-800">{t('userSettingsPage.terminals.processing')}</div>
                      <div className="text-xs text-gray-500 mt-1">{t('userSettingsPage.terminals.processingHelp')}</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {TERMINAL_NUMERIC_FIELDS.map((field) => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t(`userSettingsPage.terminals.${field}`)}
                          </label>
                          <input
                            type="number"
                            step="0.5"
                            value={terminalModal.draft[field]}
                            onChange={(e) => handleTerminalModalChange(field, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {terminalModalError && (
                    <p className="text-sm text-red-600" role="alert">{terminalModalError}</p>
                  )}

                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleSaveTerminalModal}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      type="button"
                      onClick={closeTerminalModal}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleResetTerminal(terminalModal.code)}
                      className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors ml-auto"
                    >
                      {t('userSettingsPage.terminals.reset')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Machines */}
          {activeSection === 'machines' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('userSettingsPage.machines.title')}</h2>
              <p className="text-gray-600 text-sm">
                {t('userSettingsPage.machines.desc')}
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {availableMachines.map(machine => {
                  const isEnabled = !!machineSettings.enabled?.[machine.id];
                  return (
                    <div key={machine.id} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{machine.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{machine.description}</p>
                        </div>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(event) => handleMachineToggle(machine.id, event.target.checked)}
                            className="sr-only peer"
                          />
                          <span className="relative w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                        </label>
                      </div>
                      <div className={`mt-4 text-sm font-medium ${isEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                        {isEnabled ? t('userSettingsPage.machines.enabledState') : t('userSettingsPage.machines.disabledState')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeSection === 'machine-ew3035' && machineSettings.enabled?.['EW-3035'] && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">EW-3035+L</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {t('userSettingsPage.machineEw3035.desc')}
                    </p>
                  </div>

              <EW3035AdvancedSettingsPanel
                settings={ew3035AdvancedSettings}
                setSettings={setEW3035AdvancedSettings}
              />

              <div>
                <h4 className="text-lg font-semibold text-gray-800">{t('userSettingsPage.machineEw3035.cableSections')}</h4>
                <p className="text-gray-600 text-sm mt-1">
                  {t('userSettingsPage.machineEw3035.sectionsDesc')}
                </p>
              </div>
              
              {localSections.SEZIONI && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-800 border-b border-gray-200">
                    {t('userSettingsPage.machineEw3035.metricSections')}
                  </div>
                  <div className="divide-y divide-gray-200">
                    {localSections.SEZIONI.map((sec, idx) => (
                      <div key={idx} className="grid grid-cols-6 gap-3 p-4 items-center hover:bg-gray-50">
                        <div className="font-medium text-gray-800">{t('userSettingsPage.machineEw3035.value')}: {sec.val}</div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Dia</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.dia || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'dia', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Back1</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.back1 || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'back1', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampL</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapL || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'clampGapL', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampR</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapR || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'clampGapR', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">WordSize</label>
                          <input
                            type="number"
                            value={sec.wordSize || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'wordSize', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {localSections.SEZIONI_AWG && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-800 border-b border-gray-200">
                    {t('userSettingsPage.machineEw3035.awgSections')}
                  </div>
                  <div className="divide-y divide-gray-200">
                    {localSections.SEZIONI_AWG.map((sec, idx) => (
                      <div key={idx} className="grid grid-cols-7 gap-3 p-4 items-center hover:bg-gray-50 text-sm">
                        <div className="font-medium text-gray-800">{t('userSettingsPage.machineEw3035.awg')}: {sec.val}</div>
                        <div className="text-gray-600 text-xs">{t('userSettingsPage.machineEw3035.metric')}: {sec.metric}</div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Dia</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.dia || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'dia', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Back1</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.back1 || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'back1', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampL</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapL || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'clampGapL', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampR</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapR || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'clampGapR', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">WordSize</label>
                          <input
                            type="number"
                            value={sec.wordSize || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'wordSize', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
                </div>
              )}

          {activeSection === 'machine-ew05' && machineSettings.enabled?.['EW-05'] && (
                <div className="border border-gray-200 rounded-lg bg-white p-5">
                  <h3 className="text-xl font-bold text-gray-800">EW-05</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t('userSettingsPage.machineEw05.desc')}
                  </p>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {(MACHINES['EW-05']?.computeSections ? [t('userSettingsPage.machineEw05.general'), 'OUTER', 'INNER', 'OUTER 2', 'SLITTING'] : []).map(sectionName => (
                      <div key={sectionName} className="border border-gray-200 rounded-md px-3 py-2 bg-gray-50 font-medium text-gray-700">
                        {sectionName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
        </div>
      </div>

      {folderPickerProvider && (
        <CloudFolderPickerModal
          providerId={folderPickerProvider}
          onClose={() => setFolderPickerProvider('')}
          onSelect={(folder) => handleFolderSelected(folderPickerProvider, folder)}
        />
      )}
    </div>
  );
};
